import { useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useLDClient, useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Userpilot } from 'userpilot';
import {
  investmentRoundFormattersStateJ,
  investmentRoundSummariesMapStateJ,
} from '../pages/Finance2/state/FinanceStateJ';
import { MaggieHttp } from '../services/MaggieHttp';
import {
  fundsByIdMapAtom,
  initConfigsWithMeta,
  roundsByIdMapAtom,
  sectorsByIdMapAtom,
  transactionTypesByIdMapAtom,
  usersByIdMapAtom,
} from '../services/state/AppConfigStateJ';
import { authenticatedUserState } from '../services/state/AuthState';
import {
  appConfigRefreshAtom,
  appConfigRefreshErrorAtom,
  appConfigRequestAtom,
  preferencesRefreshAtom,
  preferencesRequestAtom,
} from '../services/state/BootStateJ';
import { IAppConfigDataModel } from '../data-models/app-config.data-model';
import { searchApiConfigAtom } from '../services/state/SearchConfigStateJ';
import { basic_formatters } from '../test-utils/test-scenarios/app-config.scenario';
import { LoadingStatus } from '../types';
import { AgFormatterService } from '../util/ag-formatter-service';
import { ColourSchemeGenerator } from '../util/ColourSchemeGenerator';
import { setRumOrgContext, setRumUser } from '../util/datadogContext';
import { httpStatus } from '../util/datadogDataLayer';
import { getEnvironment, isDemo, isDevelopment, isLocal } from '../util/environment';
import { FeatureFlags, MaggieFeatureFlags } from '../util/feature-flags';
import { defaultFormatter, FormatterService } from '../util/formatter-service';
import { FundFormatter } from '../util/formatters/FundFormatter';
import { RoundFormatter } from '../util/formatters/RoundFormatter';
import { SectorFormatter } from '../util/formatters/SectorFormatter';
import { TransactionTypeFormatter } from '../util/formatters/TransactionTypeFormatter';
import { UserFormatter } from '../util/formatters/UserFormatter';
import { getForesightStore } from '../util/jotai-store';
import { primeFormulaFields, primeLandingData } from '../util/landingPrefetch';
import { getLdFlag, identifyLdUser, setLdClient } from '../util/ld-client';
import { ParserService } from '../util/parser-service';
import { FundParser } from '../util/parsers/FundParser';
import { PreferencesServiceV3 } from '../util/preferences-service-v3';

import { transactionTypeServiceState } from '../util/transaction-type-service';
import { logout } from '../services/hooks/useAuthActions';
import { useCleanLocalStorageShortcut } from './useCleanLocalStorageShortcut';
import { useCopyTokenToConsole } from './useCopyTokenToConsole';
import { useInitSocket } from './useInitSocket';

const emailDomainRegex = /(.*)@(.*)(.com)/;

export function useInitApp() {
  const colourGenStatus = useInitColourGenerator();
  const connectionStatus = useInitConnections();
  const appConfigStatus = useInitAppConfig(connectionStatus);
  const launchDarklyStatus = useInitLaunchDarkly();
  const preferencesStatus = useInitPreferencesServiceV3(connectionStatus);

  useInitSentry();
  useInitDatadogRum();
  useInitPendo();
  useInitUserPilot();

  useCopyTokenToConsole();
  useCleanLocalStorageShortcut();
  useInitSocket();

  return useMemo(() => {
    const appOverallStatus = getAggregateStatus([
      appConfigStatus,
      colourGenStatus,
      connectionStatus,
      launchDarklyStatus,
      preferencesStatus,
    ]);

    return {
      appConfig: appConfigStatus,
      colorGenerator: colourGenStatus,
      connectionStatus: connectionStatus,
      overallStatus: appOverallStatus,
    };
  }, [appConfigStatus, colourGenStatus, connectionStatus, launchDarklyStatus, preferencesStatus]);
}

function useInitColourGenerator() {
  const theme = useTheme();
  ColourSchemeGenerator.init(theme.colors);

  return LoadingStatus.success;
}

function useInitSentry() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    if (isLocal() || isDevelopment() || !import.meta.env.VITE_SENTRY_DSN || !user?.email) {
      return;
    }

    Sentry.setUser({
      email: user?.email,
    });
  }, [user]);
}

export function useInitDatadogRum() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    if (!user) {
      return;
    }

    setRumUser({ id: user.id, email: user.email });
    setRumOrgContext(user.tenantId);
  }, [user]);
}

function useInitConnections() {
  const user = useAtomValue(authenticatedUserState);
  const store = useStore();
  const [status, setStatus] = useState(LoadingStatus.loading);

  useEffect(() => {
    if (user === undefined || status !== LoadingStatus.loading) {
      return;
    }

    // Already done when the early boot started from the access token.
    MaggieHttp.initForSession(store);

    setStatus(LoadingStatus.success);
  }, [status, store, user]);

  return status;
}

export function useInitAppConfig(connectionStatus: LoadingStatus) {
  const [status, setStatus] = useState(LoadingStatus.idle);
  const initFormatters = useInitFormatters();
  const initServices = useInitServices();
  const initParsers = useInitParsers();
  // Set when the boot started from a cached configuration and the server's differs.
  const refreshedConfig = useAtomValue(appConfigRefreshAtom);

  const applyConfig = useCallback(
    (configMeta: IAppConfigDataModel) => {
      try {
        initConfigsWithMeta(getForesightStore(), cloneDeep(configMeta));

        const formatters = [...basic_formatters(), ...configMeta.formatters];
        // A refreshed configuration finds the service in place. Its formatters are updated there, so the
        // ones pages registered since (deal stages, KPI templates) stay.
        if (FormatterService.isInitialized()) {
          FormatterService.get().init(formatters);
        } else {
          FormatterService.initService(formatters);
        }
        AgFormatterService.initService();
        ParserService.initService();

        return Promise.all([initFormatters(), initServices(), initParsers()]);
      } catch (err) {
        console.error(err);
        FormatterService.initService(basic_formatters());
      }
    },
    [initFormatters, initParsers, initServices]
  );

  useEffect(() => {
    if (status === LoadingStatus.success && refreshedConfig) {
      void applyConfig(refreshedConfig);
    }
  }, [applyConfig, refreshedConfig, status]);

  // A configuration read that fails ends the boot: a refused session signs out, once, and anything else
  // puts up the error screen.
  const failBoot = useCallback((error: unknown) => {
    const responseStatus = httpStatus(error);
    if (responseStatus === 401 || responseStatus === 403) {
      const guard = `fs_${responseStatus}_logout`;
      if (!sessionStorage.getItem(guard)) {
        sessionStorage.setItem(guard, '1');
        logout();
        return;
      }
    }
    setStatus(LoadingStatus.error);
  }, []);

  // The read behind a cached boot failed. It ends the boot as a direct read's failure would, once the cached
  // boot has finished, so its success cannot land after the error. The failure stays recorded: a boot that
  // starts again on this page meets it too, rather than the cached value it is about.
  const refreshError = useAtomValue(appConfigRefreshErrorAtom);
  useEffect(() => {
    if (status === LoadingStatus.success && refreshError !== null) {
      failBoot(refreshError);
    }
  }, [failBoot, refreshError, status]);

  useEffect(() => {
    if (status === LoadingStatus.idle && connectionStatus === LoadingStatus.success) {
      setStatus(LoadingStatus.loading);

      // The round summaries only need the token, not the config, so they load alongside it. The boot
      // does not wait for them: the grids that show rounds do, through investmentRoundFormattersStateJ.
      // The full GET /round waits until after the boot (primeAfterBoot). Both requests are already in
      // flight when the early boot started them.
      const store = getForesightStore();
      void store.get(investmentRoundSummariesMapStateJ).catch(() => undefined);
      store
        .get(appConfigRequestAtom)
        .then(applyConfig)
        .then(() => {
          setStatus(LoadingStatus.success);
        })
        .catch(failBoot);
    }
  }, [applyConfig, connectionStatus, failBoot, status]);

  return status;
}

function useInitPendo() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    const email = user?.email ?? '';

    if (isLocal() || !email) {
      return;
    }

    const domain = email.match(emailDomainRegex)?.[2] ?? email;
    window.pendo.initialize({
      visitor: {
        id: email,
        environment: getEnvironment(),
      },
      account: {
        id: domain,
      },
    });
  }, [user?.email]);
}

function useInitUserPilot() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    const userPilotEnabled = isDemo() && user && import.meta.env.VITE_USER_PILOT_TOKEN;

    if (!userPilotEnabled) {
      return;
    }

    Userpilot.initialize(import.meta.env.VITE_USER_PILOT_TOKEN);
    Userpilot.identify(user.email, {
      name: user.name,
      email: user.email,
      created_at: user.createdAt,
    });
  }, [user]);
}

export function useInitLaunchDarkly() {
  const [status, setStatus] = useState(LoadingStatus.loading);
  const user = useAtomValue(authenticatedUserState);
  // The context is the email alone, so a new user object for the same person (a renewed token) does not
  // identify again.
  const email = user ? user.email : undefined;
  const ldClient = useLDClient();
  const { showSourcing } = useFlags<MaggieFeatureFlags>();
  const setSearchApiConfig = useSetAtom(searchApiConfigAtom);

  useEffect(() => {
    if (email === undefined || !ldClient) {
      return;
    }

    // Shares the early boot's identify when it ran for the same email, and makes its own if that one failed.
    identifyLdUser(ldClient, email)
      .catch(() => identifyLdUser(ldClient, email))
      .then(() => {
        // Store client for use outside React (see getLdFlag in src/util/ld-client.ts)
        setLdClient(ldClient);
        setStatus(LoadingStatus.success);
        // With the flags in, the shell's and the landing page's requests start now, alongside the rest
        // of the boot, instead of after the full-page loader clears. After the status, and caught: a
        // head start must not be able to hold the boot or reject this chain.
        try {
          primeLandingData(window.location.pathname);
          primeFormulaFields(getLdFlag(FeatureFlags.showFormulaFields, false));
        } catch {
          // the shell makes the same requests itself
        }
      });
  }, [ldClient, email]);

  useEffect(() => {
    setSearchApiConfig({
      portfolio: true,
      sourcing: showSourcing === true,
    });
  }, [showSourcing, setSearchApiConfig]);

  return status;
}

function getAggregateStatus(statuses: LoadingStatus[]) {
  for (const status of statuses) {
    if (status === LoadingStatus.error) {
      return LoadingStatus.error;
    }
    if (status === LoadingStatus.loading) {
      return LoadingStatus.loading;
    }
  }

  return LoadingStatus.success;
}

export function useInitParsers() {
  return useAtomCallback(
    useCallback(async (get) => {
      const fundsById = get(fundsByIdMapAtom);
      const parserService = ParserService.get();
      parserService.setParserForId('fund', new FundParser().init(fundsById).parse);
    }, [])
  );
}

export function useInitFormatters() {
  return useCallback(() => {
    const store = getForesightStore();
    const fundsById = store.get(fundsByIdMapAtom);
    const roundsById = store.get(roundsByIdMapAtom);
    const sectorsById = store.get(sectorsByIdMapAtom);
    const transactionTypesById = store.get(transactionTypesByIdMapAtom);
    const usersById = store.get(usersByIdMapAtom);
    const transTypeFormatter = new TransactionTypeFormatter().init(transactionTypesById);
    const service = FormatterService.get();

    service.setFormatterForId('fund', new FundFormatter().init(fundsById).format);
    service.setFormatterForId('round', new RoundFormatter().init(roundsById).format);
    service.setFormatterForId('sector', new SectorFormatter().init(sectorsById).format);
    service.setFormatterForId('transactionType', transTypeFormatter.format);
    service.setFormatterForId('transactionCategory', transTypeFormatter.formatCategory);
    service.setFormatterForId('transactionSubType', transTypeFormatter.formatSubType);
    service.setFormatterForId('user', new UserFormatter().init(usersById).format);
    service.setFormatterForId('userByEmail', new UserFormatter().init(usersById).formatByEmail);

    // The boot does not wait for GET /round/summary. These blanks stand in until it lands; the grids
    // that show rounds wait for investmentRoundFormattersStateJ, which registers the real formatters.
    // If it fails, the ids are shown rather than nothing.
    service.setFormatterForId('investmentRound', () => '');
    service.setFormatterForId('stage', () => '');
    void store.get(investmentRoundFormattersStateJ).catch((error) => {
      console.error(error);
      service.setFormatterForId('investmentRound', defaultFormatter);
      service.setFormatterForId('stage', defaultFormatter);
    });
  }, []);
}

export function useInitPreferencesServiceV3(connectionStatus: LoadingStatus) {
  const [status, setStatus] = useState(LoadingStatus.loading);
  const initPrefs = useCallback(async () => {
    try {
      const preferences = await getForesightStore().get(preferencesRequestAtom);
      PreferencesServiceV3.initService(preferences);
      setStatus(LoadingStatus.success);
    } catch (error) {
      PreferencesServiceV3.initService([]);
      console.error(error);
      setStatus(LoadingStatus.success);
    }
  }, []);

  useEffect(() => {
    if (connectionStatus === LoadingStatus.success) {
      initPrefs();
    }
  }, [connectionStatus, initPrefs]);

  // The server's preferences, when the boot started from cached ones that differ. A preference set in this
  // session already went to the server, so the response may predate it; this session keeps its own then.
  // Preferences already read keep their cached values until the next load.
  const refreshedPreferences = useAtomValue(preferencesRefreshAtom);
  useEffect(() => {
    if (
      status === LoadingStatus.success &&
      refreshedPreferences &&
      !PreferencesServiceV3.get().hasChangedSinceInit()
    ) {
      PreferencesServiceV3.initService(refreshedPreferences);
    }
  }, [refreshedPreferences, status]);

  return status;
}

export function useInitServices() {
  return useAtomCallback(
    useCallback((get) => {
      // Reading the atom triggers TransactionTypeService.initService as a
      // side effect (see transaction-type-service.ts).
      get(transactionTypeServiceState);
    }, [])
  );
}
